import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Archive as ArchiveIcon, RotateCcw, Users, CalendarDays, Car, Wrench, FileText, ChevronRight } from "lucide-react";
import { fmtEurCompact as fmtEur } from "@/lib/formatMoney";

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("de-DE") : "—";

const TABS = [
  { id: "customers",    label: "Kunden",      Icon: Users,        key: "customers" },
  { id: "appointments", label: "Termine",     Icon: CalendarDays, key: "appointments" },
  { id: "vehicles",     label: "Fahrzeuge",   Icon: Car,          key: "vehicles" },
  { id: "jobs",         label: "Aufträge",    Icon: Wrench,       key: "jobs" },
  { id: "invoices",     label: "Rechnungen",  Icon: FileText,     key: "invoices" },
];

export default function Archive() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("customers");
  const { data = {}, isLoading } = useQuery({
    queryKey: ["archive"],
    queryFn: async () => (await api.get("/archive")).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["archive"] });
    qc.invalidateQueries({ queryKey: ["customers"] });
    qc.invalidateQueries({ queryKey: ["appointments"] });
    qc.invalidateQueries({ queryKey: ["vehicles"] });
    qc.invalidateQueries({ queryKey: ["jobs"] });
    qc.invalidateQueries({ queryKey: ["invoices"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const restoreCustomer = useMutation({
    mutationFn: async (cid) => (await api.post(`/customers/${cid}/restore`)).data,
    onSuccess: () => { toast.success("Kunde wiederhergestellt"); invalidate(); },
    onError: (e) => toast.error(apiError(e)),
  });
  const restoreAppt = useMutation({
    mutationFn: async (aid) => (await api.post(`/appointments/${aid}/restore`)).data,
    onSuccess: () => { toast.success("Termin wiederhergestellt"); invalidate(); },
    onError: (e) => toast.error(apiError(e)),
  });
  const restoreVehicle = useMutation({
    mutationFn: async (vid) => (await api.patch(`/vehicles/${vid}/status`, { status: "active" })).data,
    onSuccess: () => { toast.success("Fahrzeug wiederhergestellt"); invalidate(); },
    onError: (e) => toast.error(apiError(e)),
  });
  const restoreJob = useMutation({
    mutationFn: async (jid) => (await api.patch(`/jobs/${jid}/status`, { status: "angelegt" })).data,
    onSuccess: () => { toast.success("Auftrag wiederhergestellt"); invalidate(); },
    onError: (e) => toast.error(apiError(e)),
  });

  const items = data[tab] || [];
  const counts = TABS.reduce((acc, t) => ({ ...acc, [t.id]: (data[t.key] || []).length }), {});

  return (
    <div className="stellar-page">
      <div className="stellar-page-heading stellar-heading-slate mb-5 sm:mb-7 flex items-center gap-3">
        <span className="stellar-module-icon stellar-module-slate"><ArchiveIcon className="h-5 w-5" /></span>
        <div>
          <h1 className="font-display text-xl sm:text-3xl font-bold tracking-tight">Archiv</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Gelöschte, archivierte und stornierte Datensätze wiederherstellen oder einsehen.</p>
        </div>
      </div>

      <div className="stellar-segmented mb-5">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`archive-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded transition-colors whitespace-nowrap ${
              tab === t.id ? "stellar-segment-active-slate font-medium" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <t.Icon className="h-3.5 w-3.5" /> {t.label}
            <span className="text-xs text-muted-foreground">({counts[t.id] || 0})</span>
          </button>
        ))}
      </div>

      <div className="premium-card overflow-hidden stellar-archive-list">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Laden…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <ArchiveIcon className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
            <div className="text-sm text-muted-foreground">Keine archivierten Einträge in dieser Kategorie.</div>
          </div>
        ) : tab === "customers" ? (
          <ul className="divide-y divide-border/60">
            {items.map((c) => (
              <li key={c.id} className="stellar-archive-row p-4 flex items-center gap-3" data-testid={`archive-customer-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {c.phone && `${c.phone} · `}Archiviert: {fmtDT(c.deleted_at)}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => restoreCustomer.mutate(c.id)} data-testid={`restore-customer-${c.id}`}>
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Wiederherstellen
                </Button>
              </li>
            ))}
          </ul>
        ) : tab === "appointments" ? (
          <ul className="divide-y divide-border/60">
            {items.map((a) => (
              <li key={a.id} className="stellar-archive-row p-4 flex items-center gap-3" data-testid={`archive-appt-${a.id}`}>
                <div className="w-1 self-stretch rounded-full" style={{ background: a.color }} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{a.customer_name} · <span className="text-muted-foreground font-normal">{a.service_name}</span></div>
                  <div className="text-xs text-muted-foreground">{fmtDT(a.start)} · {a.car_model} {a.plate}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => restoreAppt.mutate(a.id)} data-testid={`restore-appt-${a.id}`}>
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Wiederherstellen
                </Button>
              </li>
            ))}
          </ul>
        ) : tab === "vehicles" ? (
          <ul className="divide-y divide-border/60">
            {items.map((v) => (
              <li key={v.id} className="stellar-archive-row p-4 flex items-center gap-3" data-testid={`archive-vehicle-${v.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{v.manufacturer} {v.model}{v.year && <span className="font-normal text-muted-foreground"> · Bj. {v.year}</span>} <span className="font-mono text-xs text-muted-foreground ml-1">{v.license_plate}</span></div>
                  <div className="text-xs text-muted-foreground">{v.customer_name || "—"}</div>
                </div>
                <Link to={`/fahrzeuge/${v.id}`} className="text-xs text-muted-foreground hover:text-foreground mr-2">Details <ChevronRight className="h-3 w-3 inline" /></Link>
                <Button size="sm" variant="outline" onClick={() => restoreVehicle.mutate(v.id)} data-testid={`restore-vehicle-${v.id}`}>
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Wiederherstellen
                </Button>
              </li>
            ))}
          </ul>
        ) : tab === "jobs" ? (
          <ul className="divide-y divide-border/60">
            {items.map((j) => (
              <li key={j.id} className="stellar-archive-row p-4 flex items-center gap-3" data-testid={`archive-job-${j.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{j.title || j.customer_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {j.customer_name} · {j.vehicle_display || "—"} · {fmtEur(j.total_price)}
                  </div>
                </div>
                <Link to={`/auftraege/${j.id}`} className="text-xs text-muted-foreground hover:text-foreground mr-2">Details <ChevronRight className="h-3 w-3 inline" /></Link>
                <Button size="sm" variant="outline" onClick={() => restoreJob.mutate(j.id)} data-testid={`restore-job-${j.id}`}>
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Wiederherstellen
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          /* Invoices — storniert are legally immutable, view-only */
          <ul className="divide-y divide-border/60">
            {items.map((i) => (
              <li key={i.id} className="stellar-archive-row p-4 flex items-center gap-3" data-testid={`archive-invoice-${i.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono font-medium">{i.invoice_number}</div>
                  <div className="text-xs text-muted-foreground">
                    {i.customer_snapshot?.name} · {fmtEur(i.total)} · Storniert: {fmtDate(i.cancelled_at)}
                  </div>
                </div>
                <Link to={`/rechnungen/${i.id}`} className="text-xs text-muted-foreground hover:text-foreground">Ansehen <ChevronRight className="h-3 w-3 inline" /></Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {tab === "invoices" && items.length > 0 && (
        <p className="mt-3 text-xs text-muted-foreground italic">
          Stornierte Rechnungen bleiben aus rechtlichen Gründen dauerhaft im System und können nicht wiederhergestellt werden.
        </p>
      )}
    </div>
  );
}
